import type { RouteComponentProps } from '@reach/router';
import React, { useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import TaskModalContent from '@components/Modal/Content/TaskModalContent';
import RoundedButton from '@components/RoundedButton/index';
import Selector from '@components/Selector/Selector';
import ViewTab from '@components/ViewTab';

import { useGetCuratorTasks } from '@hooks/useGetCuratorTasks';
import { useModal } from '@hooks/useModal';

import { MarginDiv } from '@layouts/Layouts/BigTitleLayout';

import MainWrapper from '@views/Records/Common/MainWrapper';

import type {
  CuratorTaskModel,
  TaskOwnership as Ownership,
  TaskDateFilter,
} from 'generated/types';
import { TaskOwnership } from 'generated/types';

import MasterTaskList from './TaskList';

const messages = defineMessages({
  title: {
    id: 'tasksMain.title',
    defaultMessage: 'Tasks',
  },
  addTask: {
    id: 'tasksMain.addTask',
    defaultMessage: 'Add Task',
  },
  all: {
    id: 'tasksMain.all',
    defaultMessage: 'All',
  },
  dueToday: {
    id: 'tasksMain.dueToday',
    defaultMessage: 'Due Today',
  },
  upcoming: {
    id: 'tasksMain.upcoming',
    defaultMessage: 'Upcoming',
  },
  overdue: {
    id: 'tasksMain.overdue',
    defaultMessage: 'Overdue',
  },
  complete: {
    id: 'tasksMain.complete',
    defaultMessage: 'Complete',
  },
  allTasks: {
    id: 'tasksMain.allTasks',
    defaultMessage: 'All Tasks',
  },
  myTasks: {
    id: 'tasksMain.myTasks',
    defaultMessage: 'My Tasks',
  },
});

const Tasks: React.FC<RouteComponentProps> = () => {
  const { formatMessage } = useIntl();
  const { closeModal, openModal, setContent } = useModal();

  const tabs = [
    { id: 'ALL', value: formatMessage(messages.all) },
    {
      id: 'DUE_TODAY',
      value: formatMessage(messages.dueToday),
    },
    { id: 'UPCOMING', value: formatMessage(messages.upcoming) },
    { id: 'OVERDUE', value: formatMessage(messages.overdue) },
    { id: 'COMPLETE', value: formatMessage(messages.complete) },
  ];
  const [activeTab, setActiveTab] = useState(tabs[0]);
  const [ownership, setOwnership] = useState<Ownership>(TaskOwnership.MY);

  const { tasks, loading } = useGetCuratorTasks(
    activeTab.id as TaskDateFilter,
    ownership
  );

  const handleOpenModal = (task?: CuratorTaskModel) => {
    openModal({
      width: 400,
      props: {
        height: '300px',
      },
      children: null,
    });
    setContent({
      children: <TaskModalContent onSubmit={closeModal} task={task} />,
    });
  };

  const handleOwnershipSelection = () => {
    setOwnership((prev) =>
      prev === TaskOwnership.ALL ? TaskOwnership.MY : TaskOwnership.ALL
    );
  };

  return (
    <MainWrapper
      friendlyName={formatMessage(messages.title)}
      loading={loading}
      title={formatMessage(messages.title)}
    >
      <RoundedButton autoSize iconName="plus" onClick={() => handleOpenModal()}>
        {messages.addTask.defaultMessage}
      </RoundedButton>
      <MarginDiv>
        <Selector
          changeSelection={handleOwnershipSelection}
          isSelected={ownership === 'ALL'}
          leftLabel={formatMessage(messages.myTasks)}
          rightLabel={formatMessage(messages.allTasks)}
        />
      </MarginDiv>
      <ViewTab changeTab={setActiveTab} selected={activeTab} tabArray={tabs} />

      <MasterTaskList ownership={ownership} tasks={tasks} />
    </MainWrapper>
  );
};

export default Tasks;
